import React from 'react'
import PropTypes from 'prop-types'
import { Badge } from 'antd'
/**
 * CustomBadge component wraps AntD’s Badge to position the badge count
 * dynamically based on the number of digits and desired side.
 * It calculates an appropriate horizontal offset so that single-, double-,
 * and triple-digit counts align cleanly to the left or right of the child.
 *
 * @component
 * @param {Object} props
 * @param {'left'|'right'} [props.position='left'] – Which side of the child to anchor the badge.
 * @param {string} [props.className='custom-abt-badge custom-abt-badge-black we-custom-badge'] – CSS classes to apply.
 * @param {number} props.count – Numeric count to display in the badge.
 * @param {number} [props.offsetTop] – Vertical offset for the badge; defaults to -2px if omitted.
 * @param {React.ReactNode} props.children – The element to which the badge is attached.
 * @returns {JSX.Element}
 */
const CustomBadge = ({
	position = 'left',
	className = 'custom-abt-badge custom-abt-badge-black we-custom-badge',
	count,
	offsetTop,
	children,
	...props
}) => {
	let offsetLeft

	switch (position) {
		case 'right':
			offsetLeft = count > 99 ? 16 : count > 9 ? 12 : 11
			break
		default:
			offsetLeft = count > 99 ? -16 : count > 9 ? -12 : -11
			break
	}

	return (
		<Badge
			count={count}
			showZero
			size="small"
			offset={[offsetLeft, offsetTop ?? -2]}
			className={className}
			{...props}
		>
			{children}
		</Badge>
	)
}

CustomBadge.propTypes = {
	position: PropTypes.oneOf(['left', 'right']),
	count: PropTypes.number.isRequired,
	className: PropTypes.string,
	offsetTop: PropTypes.number,
	children: PropTypes.node.isRequired
}

// defaultProps has been removed as default parameters are used directly

export { CustomBadge }
